import axios, { type AxiosResponse } from 'axios';
import { useUserStore } from '~/stores/auth';
import { useHelperStore } from '~/stores/helpers';

export default defineNuxtPlugin(async () => {
  const { getSession } = useAuth();
  const data = await getSession();
  const piniaHelper = useHelperStore();
  const piniaUser = useUserStore();

  const token = data.user?.access_token;
  const client = axios.create({});

  client.defaults.headers.common.Authorization = `Bearer ${token}`;

  client.interceptors.request.use((config) => {
    const { data } = useAuth();
    const token = data.value?.user?.access_token;

    if (token && config.headers && 'set' in config.headers) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }

    return config;
  });

  let isRefreshing = false;
  let failedQueue: Array<{
    resolve: (value: string | PromiseLike<string>) => void;
    reject: (reason: Error) => void;
  }> = [];

  const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) prom.reject(error);
      else prom.resolve(token ?? '');
    });
    failedQueue = [];
  };

  client.interceptors.response.use(
    (response) => {
      if (response.status !== 204 && response.data == null) {
        // Generic user-facing message
        const emptyResponseError = new Error(
          'Something went wrong. Please try again.'
        );
        // Debug log for developers
        console.error('Empty response from server: ', response);
        emptyResponseError.type = 'EMPTY_RESPONSE';
        emptyResponseError.originalResponse = response;
        return Promise.reject(emptyResponseError);
      }

      const redirectWithError = async (path: string, error: string) => {
        await navigateTo(path);
        return Promise.reject(new Error(error));
      };

      if (piniaUser.user?.nonProvisionedUser) {
        return redirectWithError('/no-study-access', 'USER_NOT_PROVISIONED');
      }

      if (piniaUser.user?.nonEecUser) {
        return redirectWithError('/403', 'FORBIDDEN_USER');
      }

      const {
        data: _data,
        request: _request,
        config: _config,
        ...statusData
      } = response;
      client.latestResponseStatus = statusData;
      piniaHelper.setLatestResponseStatus(JSON.stringify(statusData));

      if (response.status >= 500) navigateTo('/500');

      return response;
    },
    async (error) => {
      const status = error?.response?.status;
      const originalRequest = error.config;

      if (status === 504) {
        const timeoutError = new Error(
          'The request timed out. Please try again.'
        );
        timeoutError.type = 'TIMEOUT';
        timeoutError.originalError = error;
        return Promise.reject(timeoutError);
      }

      // Handle 401 & 403 only for API requests
      if (
        error.response &&
        (status === 401 || status === 403) &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          });
        }

        isRefreshing = true;

        try {
          const newSession = await getSession();
          if (!newSession || !newSession.user?.access_token) {
            isRefreshing = false;
            processQueue(new Error('Session expired'), null);
            return Promise.reject(new Error('Session expired'));
          }

          const newToken = newSession.user.access_token;
          isRefreshing = false;

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);

          return client(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(
            refreshError instanceof Error
              ? refreshError
              : new Error(String(refreshError)),
            null
          );
          return Promise.reject(
            refreshError instanceof Error
              ? refreshError
              : new Error(String(refreshError))
          );
        }
      }

      return Promise.reject(error);
    }
  );

  for (const method of [
    'request',
    'delete',
    'get',
    'head',
    'options',
    'post',
    'put',
    'patch',
  ] as const) {
    client[`$${method}`] = function (
      ...args: Parameters<typeof client.request>
    ) {
      return (client[method] as typeof client.request)
        .apply(this, args)
        .then((res) => {
          const response = res as AxiosResponse;
          return response.status === 204 ? null : response.data;
        });
    };
  }

  return {
    provide: { axios: client },
  };
});
